import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { fmtDay, fmtMoney } from '../lib/format';
import ProjectStatement from './ProjectStatement';

// The admin's full view of one project: the client statement, the expense
// sheet, and the "current standing" profit figures — the three spreadsheets in
// one place. Admin-only; the homeowner never receives this payload.
export default function AdminProjectDetail({ project }) {
  const [tab, setTab] = useState('statement');
  const {
    vendors, setActiveProject,
    openProjectLine, handleDeleteProjectLine,
    openProjectPayment, openProjectExpense, handleDeleteProjectExpense,
  } = useApp();

  if (!project) return null;
  const expenses = project.expenses || [];
  const vendorName = (id) => vendors.find(v => v._id === String(id))?.businessName || 'a technician';

  // Group the expense sheet by trade, the way the spreadsheet blocks it out.
  const byTrade = {};
  expenses.forEach(e => {
    const line = (project.lines || []).find(l => String(l._id) === String(e.lineId));
    const key = line?.trade || 'MISCELLANEOUS';
    (byTrade[key] = byTrade[key] || []).push(e);
  });

  // And by supplier, which is the vendor-ledger strip down the right side.
  const bySupplier = {};
  expenses.forEach(e => {
    const key = e.supplier || 'Other Vendor';
    bySupplier[key] = (bySupplier[key] || 0) + (Number(e.amount) || 0);
  });

  const Standing = () => (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
      {[
        ['Received', project.receivedTotal, '#f1f5f9', 'var(--slate)'],
        ['Labor (tech cost)', project.laborTotal, 'var(--amber-light)', 'var(--amber)'],
        ['QPS expenses', project.qpsExpenseTotal, 'var(--red-light)', 'var(--red)'],
        ['Profit', project.profit, project.profit >= 0 ? 'var(--green-light)' : 'var(--red-light)', project.profit >= 0 ? 'var(--green)' : 'var(--red)'],
      ].map(([label, value, bg, color]) => (
        <div key={label} style={{ flex: 1, minWidth: 110, background: bg, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color, opacity: .85 }}>{label}</div>
          <div style={{ fontSize: 19, color }}>{fmtMoney(value)}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }} onClick={() => setActiveProject(null)}>← All projects</button>

      <div className="lead-card" style={{ cursor: 'default' }}>
        <div className="lead-name">{project.title}</div>
        <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 3 }}>
          {project.homeownerId?.name || project.clientName || 'No client linked'}
          {project.address && <> · {project.address}</>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, margin: '14px 0' }}>
        {[['statement', 'Statement'], ['expenses', 'Expenses'], ['standing', 'Profit']].map(([k, label]) => (
          <button key={k} className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>

      {tab === 'statement' && (
        <>
          <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }} onClick={() => openProjectLine(project, null)}>+ Add Invoice Line</button>
          <ProjectStatement
            project={project}
            isAdmin
            onRecordPayment={(line) => openProjectPayment(project, line)}
            onEditLine={(line) => openProjectLine(project, line)}
            onDeleteLine={(line) => handleDeleteProjectLine(project, line)}
          />
        </>
      )}

      {tab === 'expenses' && (
        <>
          <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }} onClick={() => openProjectExpense(project)}>+ Log an Expense</button>

          {expenses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)', lineHeight: 1.5 }}>
              No expenses logged yet. Every receipt you add here comes off the project's profit — and if you tag it
              to a technician, it comes off what you owe them instead.
            </div>
          ) : (
            <>
              {Object.entries(byTrade).map(([trade, list]) => (
                <div key={trade} className="lead-card" style={{ cursor: 'default' }}>
                  <div style={{ fontSize: 13, marginBottom: 6 }}>
                    {trade}
                    <span style={{ float: 'right', color: 'var(--red)' }}>
                      {fmtMoney(list.reduce((t, e) => t + (Number(e.amount) || 0), 0))}
                    </span>
                  </div>
                  {list.map(e => (
                    <div key={e._id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '5px 0', borderTop: '1px dashed var(--border)' }}>
                      <span style={{ color: 'var(--muted)', minWidth: 70 }}>{fmtDay(e.date)}</span>
                      <span>
                        {e.supplier}{e.description ? ` - ${e.description}` : ''}
                        {e.chargeToVendorId && (
                          <span className="tag tag-amber" style={{ marginLeft: 6 }}>charged to {vendorName(e.chargeToVendorId)}</span>
                        )}
                      </span>
                      <span style={{ marginLeft: 'auto' }}>{fmtMoney(e.amount)}</span>
                      {e.receiptUrl && <button className="btn btn-ghost btn-sm" onClick={() => window.open(e.receiptUrl, '_blank')}>receipt</button>}
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDeleteProjectExpense(project, e)}>×</button>
                    </div>
                  ))}
                </div>
              ))}

              <div className="sec-label" style={{ marginTop: 16 }}>Spend by supplier</div>
              <div className="lead-card" style={{ cursor: 'default' }}>
                {Object.entries(bySupplier).sort((a, b) => b[1] - a[1]).map(([supplier, total]) => (
                  <div key={supplier} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px dashed var(--border)' }}>
                    <span>{supplier}</span><span>{fmtMoney(total)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, paddingTop: 8 }}>
                  <span>Total spent</span><span style={{ color: 'var(--red)' }}>{fmtMoney(project.expenseTotal)}</span>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'standing' && (
        <>
          <Standing />
          <div className="lead-card" style={{ cursor: 'default' }}>
            <div className="sec-label">How the profit is worked out</div>
            {[
              ['Invoiced to the client', project.invoicedTotal, 'var(--navy)'],
              ['Less what the techs quoted', -project.laborTotal, 'var(--red)'],
              ['Less supplies QPS absorbed', -project.qpsExpenseTotal, 'var(--red)'],
            ].map(([label, value, color]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <span>{label}</span><span style={{ color }}>{fmtMoney(value)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, paddingTop: 10 }}>
              <span>Profit</span>
              <span style={{ color: project.profit >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmtMoney(project.profit)}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10, lineHeight: 1.5 }}>
              Supplies tagged to a technician aren't counted here — those come off that technician's pay instead,
              so counting them again would understate the profit.
            </div>
          </div>

          <div className="sec-label" style={{ marginTop: 16 }}>Still to collect</div>
          <div className="lead-card" style={{ cursor: 'default' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <span>Owed by the client</span>
              <span style={{ color: project.owedByClient > 0 ? 'var(--red)' : 'var(--green)' }}>{fmtMoney(project.owedByClient)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
